import React, { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../ui/tabs';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { toast } from 'sonner';
import { Assessment } from '.';

ChartJS.register(ArcElement, Tooltip, Legend);

const sidebarVariants = {
    hidden: { x: '100%', opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: '100%', opacity: 0 },
};
export interface UserResult {
  score: number;
  report: {
    score: number;
    communicationScore: number;
    overallSummary: {
      conclusion: string;
      interviewSummary: string[]; // contains HTML strings
      communicationSummary: string[]; // contains HTML strings
    };
    reportSkills: {
      skill: string;
      score: number;
    }[];
  };
  proctoringEventsData?: {
    proctoringEvents: {
      proctoringScore: number;
      blurEventCount: number;
      disableCameraEventCount: number;
      muteEventCount: number;
      proctoringLogs: {
        event: {
          eventType: string;
          eventTime: {
            clockTime: string; // ISO timestamp string
            interviewTime?: number; // optional
          };
          metadata?: {
            ipAddress?: string;
          };
        };
        eventTimestamp: number; // in seconds
      }[];
    };
  };
}


const UserResultsSidebar = ({ result, selectedCandidate, recording, transcript, onClose } : any) => {
    const [assessment, setAssessment] = useState<UserResult | null>(null);

    useEffect(() => {
        if (result?.success && result.data) {
            setAssessment(result.data);
        } else {
            toast.error('Invalid result data');
        }
    }, [result]);

    if (!assessment) return null;

    const getScoreColor = (score: number) => {
        if (score >= 7) return 'bg-green-100 text-green-800';
        if (score >= 4) return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    const getPieData = (skills :any) => {
        if (!skills || skills.length === 0) return { labels: [], datasets: [] };
        return {
            labels: skills.map((skill: any) => skill.skill),
            datasets: [{
                data: skills.map((skill: any) => skill.score || 0),
                backgroundColor: ['#4CAF50', '#FF6384', '#36A2EB'],
                borderColor: '#fff',
                borderWidth: 1,
            }],
        };
    };

    const pieOptions = {
        responsive: true,
        plugins: {
            legend: { position: "bottom" as const, labels: { boxWidth: 8, font: { size: 12 } } },
            tooltip: { enabled: true },
        },
        maintainAspectRatio: false,
    };
const formatTime = (seconds : number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};
    return (
        <AnimatePresence mode="wait">
            <>
                <motion.div
                    style={{ marginTop: '0px' }}

                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.5 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 bg-black z-40"
                    onClick={onClose}
                />
                <motion.div
                    variants={sidebarVariants}
                    style={{ marginTop: '0px' }}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="fixed top-0 right-0 h-full w-[50%] bg-white border-l border-gray-200 z-50 shadow-lg overflow-y-auto"
                >
                    {
                        assessment?
                    <Card className="h-full flex flex-col">
                        <CardHeader className="flex flex-row items-center justify-between p-4 border-b">
                            <CardTitle className="text-lg font-semibold">Assessment Results</CardTitle>
                            <Button variant="ghost" size="icon" onClick={onClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-y-auto p-4 space-y-6">
                            <Tabs defaultValue="overview" className="w-full">
                                <TabsList className="grid w-full grid-cols-4">
                                    <TabsTrigger value="overview">Overview</TabsTrigger>
                                    <TabsTrigger value="skills">Skills</TabsTrigger>
                                    <TabsTrigger value="proctoring">Proctoring</TabsTrigger>
                                    <TabsTrigger value="recording">Recording</TabsTrigger>
                                </TabsList>

                                {/* Overview Tab */}
                                <TabsContent value="overview">
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-4">
                                            <Avatar className="w-20 h-20 bg-gray-200">
                                                <AvatarFallback className="text-xl">
                                                    {selectedCandidate.firstName?.slice(0, 2).toUpperCase() || 'C'}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <h3 className="text-xl font-semibold">Interview of {selectedCandidate.firstName}</h3>
                                                <Badge className={getScoreColor(assessment.score)}>
                                                    Score: {assessment.report.score}/10
                                                </Badge>
                                            </div>
                                        </div>
                                        <Separator />
                                        <div className="space-y-2">
                                            <p className="text-sm font-medium">Communication Score: {assessment.report.communicationScore}/10</p>
                                            <p className="text-sm"><span className="font-medium">Overall Summary:</span> {assessment.report.overallSummary.conclusion}</p>
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* Skills Tab */}
                                <TabsContent value="skills">
                                    <div className="space-y-4">
                                        <div className="h-64">
                                            <Pie
                                                data={getPieData(assessment.report.reportSkills)}
                                                options={pieOptions}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            {assessment.report.reportSkills.map((skill, index) => (
                                                <div key={index} className="flex justify-between items-center">
                                                    <span className="text-sm font-medium">{skill.skill}</span>
                                                    <Badge className={getScoreColor(skill.score)}>{skill.score}/10</Badge>
                                                </div>
                                            ))}
                                        </div>
                                        <Separator />
                                        <div className="space-y-2">
                                            <h4 className="text-sm font-medium">Interview Summary</h4>
                                            <ul className="list-disc list-inside text-sm">
                                                {[...assessment.report.overallSummary.interviewSummary]
                                                    .sort((a, b) => {
                                                        const isGreenA = a.includes('<span style="color: green;">');
                                                        const isGreenB = b.includes('<span style="color: green;">');
                                                        return isGreenA === isGreenB ? 0 : isGreenA ? -1 : 1;
                                                    })
                                                    .map((summary, idx) => (
                                                        <li key={idx} dangerouslySetInnerHTML={{ __html: summary }} />
                                                    ))}
                                            </ul>
                                            <h4 className="text-sm font-medium">Communication Summary</h4>
                                          <ul className="list-disc list-inside text-sm">
  {[...(assessment?.report?.overallSummary?.communicationSummary || [])]
    .sort((a, b) => {
      const isGreenA = a.includes('<span style="color: green;">');
      const isGreenB = b.includes('<span style="color: green;">');
      return isGreenA === isGreenB ? 0 : isGreenA ? -1 : 1;
    })
    .map((summary, idx) => (
      <li key={idx} dangerouslySetInnerHTML={{ __html: summary }} />
    ))}
</ul>

                                        </div>
                                    </div>
                                </TabsContent>

                                {/* Proctoring Tab */}
                                <TabsContent value="proctoring">
                                    <div className="space-y-4">
                                        <div className="flex flex-wrap justify-between gap-4">
                                            <div className="flex items-center gap-3 bg-blue-100 rounded-xl p-4 shadow-sm w-full sm:w-[250px]">
                                                <span className="text-yellow-500 text-xl">📊</span>
                                                <div>
                                                    <p className="text-sm text-gray-700">Proctoring Score</p>
                                                    <p className="text-lg font-semibold text-gray-900">
                                                        {assessment?.proctoringEventsData?.proctoringEvents?.proctoringScore}/10
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3 bg-green-100 rounded-xl p-4 shadow-sm w-full sm:w-[250px]">
                                                <span className="text-yellow-500 text-xl">🖥️</span>
                                                <div>
                                                    <p className="text-sm text-gray-700">Blur Events</p>
                                                    <p className="text-lg font-semibold text-gray-900">
                                                        {assessment?.proctoringEventsData?.proctoringEvents?.blurEventCount}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3 bg-yellow-100 rounded-xl p-4 shadow-sm w-full sm:w-[250px]">
                                                <span className="text-yellow-500 text-xl">📷</span>
                                                <div>
                                                    <p className="text-sm text-gray-700">Camera Disabled</p>
                                                    <p className="text-lg font-semibold text-gray-900">
                                                        {assessment?.proctoringEventsData?.proctoringEvents?.disableCameraEventCount}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3 bg-red-100 rounded-xl p-4 shadow-sm w-full sm:w-[250px]">
                                                <span className="text-yellow-500 text-xl">🔇</span>
                                                <div>
                                                    <p className="text-sm text-gray-700">Mute Events</p>
                                                    <p className="text-lg font-semibold text-gray-900">
                                                        {assessment?.proctoringEventsData?.proctoringEvents?.muteEventCount}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <Separator />
                                        <div className="text-sm">
                                            <span className="font-medium">Proctoring Logs:</span>
                                            <Card className="mt-2 overflow-x-auto">
                                                <CardContent className="p-0">
                                                    <table className="w-full text-sm border-collapse">
                                                        <thead>
                                                            <tr className="bg-gray-100">
                                                                <th className="border p-2 text-left">Event Type</th>
                                                                <th className="border p-2 text-left">Clock Time (IST)</th>
                                                                <th className="border p-2 text-left">Interview Time (s)</th>
                                                                <th className="border p-2 text-left">Timestamp (s)</th>
                                                                <th className="border p-2 text-left">IP Address</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {assessment?.proctoringEventsData?.proctoringEvents.proctoringLogs.map((log, idx) => {
                                                                const clockTimeIST = new Date(log.event.eventTime.clockTime);
                                                                clockTimeIST.setMinutes(clockTimeIST.getMinutes() + 330); // Convert UTC to IST (UTC+5:30)
                                                                return (
                                                                    <tr key={idx} className="hover:bg-gray-50">
                                                                        <td className="border p-2">{log.event.eventType}</td>
                                                                        <td className="border p-2">
                                                                            {clockTimeIST.toLocaleString('en-IN', {
                                                                                year: 'numeric',
                                                                                month: 'short',
                                                                                day: 'numeric',
                                                                                hour: '2-digit',
                                                                                minute: '2-digit',
                                                                                second: '2-digit',
                                                                                timeZoneName: 'short',
                                                                            })}
                                                                        </td>
                                                                        <td className="border p-2">{log.event.eventTime.interviewTime || 'N/A'}</td>
                                                                        <td className="border p-2">{log.eventTimestamp.toFixed(3)}</td>
                                                                        <td className="border p-2">{log.event.metadata?.ipAddress || 'N/A'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </div>
                                </TabsContent>

                                   <TabsContent value="recording">
                                    <div className="space-y-4">
                                        {
                                            recording ?
                                        <video src={recording} controls={true}/>
                                        :
                                        <p>Recording not found</p>
                                        }
                                       {transcript.length > 0 ? (
                                        <div className="space-y-4">
                                            <h2 className="text-xl font-semibold mb-4">Transcript</h2>
                                            {transcript.map((line : any, index : number) => {
                                                
                                                const startTime = formatTime(line.start);
                                                const endTime = formatTime(line.end);
                                                const duration = line.end - line.start;
                                                const timetaken = formatTime(duration);
                                                return(
                                            <div
                                                key={index}
                                                style={{width: "100%"}}
                                                className={`p-4 rounded-xl shadow-md ${
                                                line.speaker === 0
                                                    ? "bg-blue-100 self-start text-left"
                                                    : "bg-green-100 self-start text-left"
                                                }`}
                                            >
                                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                                <p className="text-sm text-gray-600">
                                                    <strong>
                                                    {line.speaker === 0
                                                        ? `AI bot (${startTime} - ${endTime})`
                                                        : `${selectedCandidate.firstName} (${startTime} - ${endTime})`}
                                                    :
                                                    </strong> 
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        {timetaken}
                                                    </p>
                                                    </div>
                                                <p className="whitespace-pre-wrap text-gray-800">{line.text}</p>
                                            </div>
                                            )})}
                                        </div>
                                        ) : (
                                        <p>Transcript not found</p>
                                        )}
                                    </div>
                                </TabsContent>



                            </Tabs>
                        </CardContent>
                    </Card>:<p>ffffffffff</p>
                    }

                </motion.div>
            </>
        </AnimatePresence>
    );
};

export default UserResultsSidebar;